import React from 'react'
import { useMedia, useMemo } from 'sb-hooks'
import { string, constants } from 'common/helpers'
import cx from 'classnames'

import type { GridProps, GridColumnsSize, GridMediaColumnsSize } from '../../Grid/Grid'

import s from './Cell.scss'


export type GridCellProps = {
  className?: string
  tag?: string
  role?: string
  tabIndex?: number
  ariaLabelledby?: string
  size?: GridColumnsSize | GridMediaColumnsSize
  columns?: GridColumnsSize
  gutter?: GridProps['gutter']
}

const Cell: React.FunctionComponent<GridCellProps> = (props) => {
  const {
    children, className, tag = 'div',
    columns = 1, size = 1, gutter,
    role = 'listitem', tabIndex, ariaLabelledby,
  } = props

  const media = useMedia()

  const rootClassName = cx(s.cell, className, {
    [s[`gutter_${gutter}`]]: gutter, // gutter_40, ...
  })

  const hasMediaSizes = useMemo(() => size && typeof size === 'object', [ size ])

  const width = useMemo(() => {
    let sizeValue

    if (hasMediaSizes) {
      const mediaMatches = constants.mediaList.reduce((result, mediaType) => {
        const mediaKey = `is${string.capitalize(mediaType)}`

        result[mediaType] = media[mediaKey]

        return result
      }, {})

      const sizeKey = Object.keys(size).find((mediaSizeKey) => mediaMatches[mediaSizeKey])

      if (sizeKey) {
        sizeValue = size[sizeKey]
      }
    }
    else {
      sizeValue = size
    }

    return `${(sizeValue >= columns ? 1 : sizeValue / columns) * 100}%`
  }, [ size, columns, media, hasMediaSizes ])

  if (hasMediaSizes) {
    const props = {
      role: 'listitem',
      className: rootClassName,
      style: { flexBasis: width },
      tabIndex,
      'aria-labelledby': ariaLabelledby,
    }

    return React.createElement(tag, props, children)
  }

  const cellProps = {
    role,
    className: rootClassName,
    style: size ? { flexBasis: width, width } : null,
    tabIndex,
    'aria-labelledby': ariaLabelledby,
  }

  return React.createElement(tag, cellProps, children)
}


export default Cell
